import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  type GroupType,
  type GetCostOffsetCodeQuery,
  type GetCostOffsetCodeQueryVariables,
} from 'graphql-query-contracts';
import { getCostOffsetCodeQuery } from 'graphql-queries';
import type { CostOffsetCode } from 'domains-billing-types';

export interface FetchCostOffsetCodeParams {
  id: string;
  groupId: string;
  groupType: GroupType;
}

export interface UseCostOffsetCodeResult {
  fetchCostOffsetCode: (params: FetchCostOffsetCodeParams) => Promise<void>;
  costOffsetCode: CostOffsetCode | undefined;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
}

export function useCostOffsetCode(): UseCostOffsetCodeResult {
  const [fetchQuery, { loading, data, error, called }] = useLazyQuery<
    GetCostOffsetCodeQuery,
    GetCostOffsetCodeQueryVariables
  >(getCostOffsetCodeQuery, {
    fetchPolicy: 'network-only',
  });

  const fetchCostOffsetCode = useCallback(
    async ({ id, groupId, groupType }: FetchCostOffsetCodeParams) => {
      await fetchQuery({
        variables: {
          getCostOffsetCodeRequest: {
            id,
            groupId,
            groupType,
          },
        },
      });
    },
    [fetchQuery]
  );

  const costOffsetCode = useMemo((): CostOffsetCode | undefined => {
    const gqlData = data?.costOffsetCode;

    if (!gqlData) {
      return undefined;
    }

    return {
      id: gqlData.id,
      code: gqlData.code,
      description: gqlData.description ?? undefined,
      groupId: gqlData.groupId,
      groupType: gqlData.groupType,
    };
  }, [data]);

  return {
    fetchCostOffsetCode,
    costOffsetCode,
    loading,
    error: error ?? undefined,
    called,
  };
}
